import { HStack, Input, Heading, IconButton, useDisclosure } from '@chakra-ui/react'
import type { FC } from 'react'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@app/components/ui/button'
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogCloseTrigger,
  DialogActionTrigger
} from '@app/components/ui/dialog'
import Can from '@app/shared/authorization/can'
import { useStore } from '@app/store'
import ICON_MAP from '@app/utils/iconMap'
import type { BusinessContext, NodeObjectInput } from '@graphql/types'

type ContextHeaderProps = {
  businessContext: Pick<BusinessContext, 'id' | 'name' | 'classType'> | null
}

const ContextHeader: FC<ContextHeaderProps> = ({ businessContext }) => {
  const [isHovered, setIsHovered] = useState(false)
  const [name, setName] = useState(businessContext?.name || '')
  const { open, onOpen, onClose } = useDisclosure()
  const updateObject = useStore.use.updateObject()
  const navigate = useNavigate()

  useEffect(() => {
    setName(businessContext?.name || '')
  }, [businessContext?.name])

  const handleSave = async () => {
    if (!businessContext) {
      return
    }

    const updatedObject = {
      [businessContext.classType]: {
        id: businessContext.id,
        name
      }
    } as NodeObjectInput
    const response = await updateObject(updatedObject)
    const data = response?.data?.domainObjectUpdate
    const bc = (data?.nodeObjects as BusinessContext[])?.[0]
    if (bc.parentBusinessContextId !== null) {
      navigate(`/maps/${bc.slug}`, { replace: true })
    }

    setIsHovered(false)
    onClose()
  }

  return (
    <HStack onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} gap={2}>
      <Heading fontSize="2xl" fontWeight="semibold" py={2}>
        {businessContext?.name}
      </Heading>
      <Can I="update" an="businessContext">
        {isHovered && (
          <IconButton aria-label="Edit name" size="xs" variant="ghost" onClick={onOpen}>
            <ICON_MAP.Edit />
          </IconButton>
        )}
      </Can>

      <DialogRoot open={open} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Name</DialogTitle>
            <DialogCloseTrigger />
          </DialogHeader>
          <DialogBody>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                handleSave()
              }}
            >
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter name" autoFocus />
            </form>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <DialogActionTrigger asChild>
              <Button onClick={handleSave}>Save</Button>
            </DialogActionTrigger>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </HStack>
  )
}

export default ContextHeader
